import { useEffect } from 'react'
import {
  HOME_DESCRIPTION,
  HOME_TITLE,
  ROBOTS_INDEX,
  ROBOTS_NOINDEX,
  SITE_NAME,
  absoluteUrl,
} from '../utils/pageMeta'

export type PageMeta = {
  title: string
  description: string
  path: string
  robots?: string
}

function upsertNamedMeta(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
  return el
}

function upsertPropertyMeta(property: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
  return el
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
  return el
}

export function useErrorPageMeta(title: string, description: string) {
  usePageMeta({
    title,
    description,
    path: window.location.pathname || '/',
    robots: ROBOTS_NOINDEX,
  })
}

export function usePageMeta({ title, description, path, robots = ROBOTS_INDEX }: PageMeta) {
  useEffect(() => {
    const previousTitle = document.title
    const canonicalHref = absoluteUrl(path)
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} — ${SITE_NAME}`

    document.title = fullTitle
    upsertNamedMeta('description', description)
    upsertNamedMeta('robots', robots)
    upsertNamedMeta('googlebot', robots)
    upsertCanonical(canonicalHref)
    upsertPropertyMeta('og:title', fullTitle)
    upsertPropertyMeta('og:description', description)
    upsertPropertyMeta('og:url', canonicalHref)
    upsertNamedMeta('twitter:title', fullTitle)
    upsertNamedMeta('twitter:description', description)

    return () => {
      document.title = previousTitle || HOME_TITLE
      upsertNamedMeta('description', HOME_DESCRIPTION)
      upsertNamedMeta('robots', ROBOTS_INDEX)
      upsertNamedMeta('googlebot', ROBOTS_INDEX)
      upsertCanonical(absoluteUrl('/'))
      upsertPropertyMeta('og:title', HOME_TITLE)
      upsertPropertyMeta('og:description', HOME_DESCRIPTION)
      upsertPropertyMeta('og:url', absoluteUrl('/'))
      upsertNamedMeta('twitter:title', HOME_TITLE)
      upsertNamedMeta('twitter:description', HOME_DESCRIPTION)
    }
  }, [title, description, path, robots])
}
